import { computed } from "vue";
import axios from "axios";
import axiosUrl from "./axios.service";
import store from "../store/index";
import moment from "moment";
import router from "../router";
import requestState from "../helpers/requestState";

const API_URL = axiosUrl.getAxiosUrl();

const isVind = computed({
  get: () => store.state.auth.env === 0 || store.state.auth.env === 40,
});
const authUser = computed(() => store.state.auth.authUser);

const hasComeFromNewsletter = computed({
  get: () => store.state.user.hasComeFromNewsletter,
});

const newsletterId = computed({
  get: () => store.state.user.newsLetterId,
});
const selectedSasCustomer = computed({
  get: () => store.state.auth.selectedSasCustomer,
});

class AuthService {
  async login(user, locale, selectedCustomerId) {
    if (
      user.username == null ||
      user.username == "" ||
      user.password == null ||
      user.password == ""
    ) {
      store.commit(
        "auth/updateModalError",
        "Gebruikersnaam en wachtwoord zijn verplicht",
      );
      store.commit("auth/enableModalError");
      return false;
    }
    const returnUrl = router.currentRoute.value.query.returnUrl || "";
    return await axios
      .post(
        API_URL + "/users/authenticate",
        {
          username: user.username,
          password: user.password,
          sasCustomerId: selectedCustomerId,
        },
        {
          params: {
            culture: locale.value,
            returnUrl: returnUrl,
          },
        },
      )
      .then(async () => {
        requestState.isLogoutInProgress = false;
        requestState.isRefreshing = false;
        requestState.isRateLimitActive = false;
        requestState.refreshPromise = null;
        await this.getAuthUserData(true, returnUrl);
        await this.handleLoginRedirect();
      })
      .catch((error) => {
        store.commit("auth/setAuthUser", null);
        store.commit("auth/setSelectedSasCustomer", null);

        if (error)
          if (error.response.data.errors != null) {
            store.commit(
              "auth/updateModalError",
              error.response.data.errors.UserName[0],
            );
          } else {
            store.commit("auth/updateModalError", error.response.data);
          }

        store.commit("auth/enableModalError");
      });
  }

  async getSasCustomers() {
    return await axios
      .get(API_URL + "/users/getUserSasCustomers")
      .then((response) => {
        if (response.status === 200) {
          store.commit("auth/setSasCustomersOptions", response.data);
          store.commit("auth/enableCustomerSelection");
        }
      });
  }

  isVindReturnUrl(returnUrl) {
    if (typeof returnUrl !== "string") return false;

    try {
      const url = new URL(returnUrl, window.location.origin);
      // If the returnUrl is not from the same origin => not Vind, it's external (SAS). If yes, it's internal redirect
      return url.origin === window.location.origin;
    } catch {
      // If parsing fails, it's likely a relative path => treat as internal
      return true;
    }
  }

  // Open-redirect guard: validates the DECODED redirect target (what location.href would receive) —
  // only Vind (same-origin) or the SAS front may be navigated to. Empty counts as "no returnUrl".
  isAllowedReturnUrl(returnUrl) {
    if (typeof returnUrl !== "string" || returnUrl === "") return true;

    let target;
    try {
      target = decodeURIComponent(returnUrl);
    } catch {
      return false; // malformed encoding — never navigate to it
    }

    try {
      const url = new URL(target, window.location.origin);
      return (
        url.origin === window.location.origin ||
        url.origin === new URL(process.env.VUE_APP_SAS_BASE_URL).origin
      );
    } catch {
      return false;
    }
  }

  handleLoginRedirect = async () => {
    let returnUrl = router.currentRoute.value.query.returnUrl || "";
    if (returnUrl.indexOf("/api") > -1) returnUrl = "";
    // open-redirect guard: the value is assigned to location.href below (decoded), so anything
    // that is not Vind (same-origin) or the SAS front is dropped → the default redirects apply
    if (!this.isAllowedReturnUrl(returnUrl)) returnUrl = "";

    if (
      !authUser.value.isVindCustomer &&
      authUser.value.customerId != selectedSasCustomer.value
    )
      // if customer is not Vind and not selected SAS customer, check language
      await this.getAppropriateLanguageBasedBaseUrl();
    // if redirectPath is set on the server-side, use that one
    if (
      authUser.value.redirect &&
      authUser.value.redirect.redirectPath != null &&
      authUser.value.redirect.redirectPath != ""
    ) {
      if (
        authUser.value.redirect.redirectPath.includes("/profile/newsletter")
      ) {
        store.commit("user/updateHasBeenRedirectedToProfile", true);
        store.commit("user/setHasConfirmedLeave", false);
      }

      if (
        authUser.value.redirect.routeName &&
        authUser.value.redirect.routeName != null &&
        authUser.value.redirect.routeName != ""
      )
        router.push({ name: authUser.value.redirect.routeName });
      else router.push(authUser.value.redirect.redirectPath);
    }
    // If the server-side conditions are not met (redirectPath is empty), we proceed checking front properties
    else {
      // this will be true only when user is not logged in, so what's the purpose of it in handling redirect after login?
      if (hasComeFromNewsletter.value) {
        router.push({
          name: "NewsletterDetails",
          params: { newsId: newsletterId.value },
        });

        store.commit("user/setHasComeFromNewsletter", true);
      } else if (
        returnUrl != null &&
        returnUrl != "undefined" &&
        returnUrl != "" &&
        returnUrl != "/"
      ) {
        // If the user belongs to multiple SAS customers and is coming from SAS, get customers options
        if (
          authUser.value.countOfSasCustomers > 1 &&
          selectedSasCustomer.value == null &&
          this.isVindReturnUrl(returnUrl) === false
        ) {
          await this.getSasCustomers();
          // If the user uses SSO or MFA, redirect them to the login page (as a logged-in user) so the options of available SAS customers can be displayed
          if (router.currentRoute.value.name !== "Login") {
            router.push({
              name: "Login",
              query: { returnUrl: returnUrl },
            });
            return;
          }
        } else {
          store.commit("auth/setSelectedSasCustomer", null);
          location.href = decodeURIComponent(returnUrl);
        }
      } else if (
        (returnUrl == null ||
          returnUrl == "undefined" ||
          returnUrl == "" ||
          returnUrl == "/") &&
        (!authUser.value.hasContent || !authUser.value.customerHasContent)
      ) {
        if (
          authUser.value.countOfSasCustomers > 1 &&
          selectedSasCustomer.value == null
        ) {
          await this.getSasCustomers();
          // If a user uses SSO or MFA, redirect them to the login page (as a logged-in user) so the options of available SAS customers can be displayed
          if (router.currentRoute.value.name !== "Login") {
            router.push({
              name: "Login",
              query: { returnUrl: returnUrl },
            });
            return;
          }
        } else {
          store.commit("auth/setSelectedSasCustomer", null);
          let baseUrl = process.env.VUE_APP_SAS_BASE_URL;
          location.href = `${baseUrl}/#${authUser.value.sasDefaultPath}`;
        }
      }
    }
  };

  async logout(serverSideLogout, returnUrl) {
    store.commit("auth/setAuthUser", null);
    store.commit("advancedSearch/resetSearchFilter", false);
    store.dispatch("chat/resetChat");
    store.commit("chat/updateShowChat", false);
    sessionStorage.clear();

    if (serverSideLogout)
      return await axios.post(API_URL + "/auth/logout", null, {
        params: {
          returnUrl: returnUrl,
        },
      });
  }

  loginMsOidc() {
    window.location.href =
      API_URL +
      "/users/authorize-oidc?frag=" +
      encodeURIComponent(window.location.hash);
  }

  async getEnvironment() {
    return await axios
      .get(API_URL + "/users/getEnvironment")
      .then(async (response) => {
        store.commit("auth/setEnvironment", response.data);
        if (
          authUser.value == null &&
          (response.data == 50 || response.data == 60)
        ) {
          await axios
            .post(API_URL + "/users/authenticateNoLogon")
            .then(async (res) => {
              requestState.isLogoutInProgress = false;
              requestState.isRefreshing = false;
              requestState.isRateLimitActive = false;
              requestState.refreshPromise = null;
              await this.getAuthUserData(false, "").then(() => {
                window.location.href = "search-grants"; // TODO: move to redirection on the server side?
              });
            });
        } else {
          const e = document.getElementById("login-page");
          if (e != null) e.style["display"] = "";
        }
      });
  }

  async getLanguageCode() {
    return await axios
      .get(API_URL + "/users/getLanguageCode")
      .then((response) => {
        store.commit("auth/setLanguageCode", response.data);
        return response.data;
      });
  }

  async getPortalTypes() {
    return await axios
      .get(API_URL + "/users/getPortalTypes")
      .then((response) => {
        store.commit("auth/setPortalThemeTypes", response.data);
      });
  }

  async getSearchInterests() {
    return await axios
      .get(API_URL + "/users/getSearchInterests")
      .then((response) => {
        store.commit("auth/setSearchInterests", response.data);
      });
  }
  async getProfessionTypes() {
    return await axios
      .get(API_URL + "/users/getProfessionTypes")
      .then((response) => {
        store.commit("auth/setProfessionTypes", response.data);
      });
  }

  async getPortalInfo() {
    return await axios
      .get(API_URL + "/users/getPortalInfo")
      .then((response) => {
        var now = moment().format("YYYY-MM-DD");
        var endDate =
          response.data.endDate != null
            ? moment(response.data.endDate).format("YYYY-MM-DD")
            : moment("2100-01-01").format("YYYY-MM-DD");

        if (!isVind.value && !moment(endDate).isSameOrAfter(now)) {
          if (response.data.environmentType == "ISV") {
            window.location.href =
              "https://www.vindsubsidies.nl/subsidieloket-voor-inwoners-is-niet-beschikbaar";
          } else {
            window.location.href =
              "https://www.vindsubsidies.nl/subsidieloket-voor-bedrijven-is-niet-beschikbaar";
          }
        }

        store.commit("auth/fillPortalCustomer", response.data);
      });
  }

  async getAppropriateLanguageBasedBaseUrl() {
    return await axios
      .get(API_URL + "/users/getAppropriateLanguageBasedBaseUrl")
      .then(async (response) => {
        if (response.data != "") {
          await this.logout(true);
          window.location.href = response.data;
        }
      });
  }

  async register(user) {
    return await axios.post(API_URL + "/users/register", {
      firstName: user.firstName,
      lastName: user.lastName,
      portalOrganisation: user.organisation,
      email: user.email,
      professionType: user.professionType,
    });
  }

  async refreshToken() {
    return await axios.post(API_URL + "/auth/refresh").catch((error) => {
      //store.commit("auth/updateModalError", error.response.data.message);
      //store.commit("auth/enableModalError");
      throw error;
    });
  }

  async getAuthUserData(includeCountOfSasCustomers, returnUrl) {
    return await axios
      .get(API_URL + "/auth/getAuthUserData", {
        params: {
          includeCountOfSasCustomers: includeCountOfSasCustomers,
          returnUrl,
        },
      })
      .then((response) => {
        store.commit("auth/setAuthUser", response.data);
      })
      .catch((error) => {
        //store.commit("auth/updateModalError", error.response.data.message);
        //store.commit("auth/enableModalError");
        throw error;
      });
  }

  async getGlobalSettings() {
    return await axios
      .get(API_URL + "/globalSettings/getGlobalSettings", {})
      .then((response) => {
        store.commit("globalSettings/updateGlobalSettings", response.data);
      })
      .catch((error) => {
        throw error;
      });
  }
}

export default new AuthService();
